import { useState, useEffect, useRef, ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useUser } from 'context/UserContext'
import { useKeyPress } from 'hooks/useKeyPress'
import { ContinueButton } from 'components/ContinueButton'
import { Heading } from 'components/Heading'
import Input from 'components/Input'
import { UnitSwitcher } from 'components/UnitSwitcher'

interface Props {
  nextRoute: string
}

export const Height: React.FC<Props> = ({ nextRoute }) => {
  const { t } = useTranslation('translation', {
    useSuspense: false,
  })

  const METRIC_IN_KEY = 1
  const METRIC_CM_KEY = 2
  const HEIGHT_UNITS = [
    { key: METRIC_IN_KEY, unit: t('height.metric_in') },
    { key: METRIC_CM_KEY, unit: t('height.metric_cm') },
  ]
  const { user, setUser } = useUser()
  const genderBasedBackground =
    user.gender === 'm'
      ? 'bg-height-male-mobile md:bg-height-male bg-center'
      : 'bg-height-female bg-center'
  const navigate = useNavigate()
  const [canContinue, setCanContinue] = useState(false)
  const [height, setHeight] = useState('')
  const [heightType, setHeightType] = useState(METRIC_CM_KEY)
  const heightInput = useRef(null)

  const onContinue = () => {
    if (!canContinue) {
      return
    }

    setUser({
      ...user,
      height,
      heightType: heightType === METRIC_IN_KEY ? 'in' : 'cm',
    })
    setTimeout(() => {
      navigate(nextRoute)
    }, 100)
  }

  const onHeightChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value

    if (/(^$|^\d{1,3}$)/.test(input)) {
      setHeight(input)
    }
  }

  useEffect(() => {
    if (height === '') {
      return
    }

    if (heightType === METRIC_IN_KEY) {
      setHeight(`${Math.round(parseInt(height, 10) / 2.54)}`)
    } else {
      setHeight(`${Math.round(parseInt(height, 10) * 2.54)}`)
    }
  }, [heightType]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCanContinue(height.length > 1)
  }, [height])

  useKeyPress('ENTER', onContinue)

  return (
    <>
      <div
        className={`default-container ${genderBasedBackground} flex flex-col`}>
        <div className="flex h-full w-full flex-col justify-between self-center overflow-auto px-7 pt-7 lg:w-5/6 xl:w-5/12">
          <Heading text={t('height.headline')} />

          <div className="z-0 mx-auto mt-5 mb-28 w-full">
            <UnitSwitcher
              units={HEIGHT_UNITS}
              selected={heightType}
              onUnitSelected={(unit) => setHeightType(unit)}
            />
            <Input
              placeholder={heightType === METRIC_CM_KEY ? '160' : '82'}
              value={height}
              id="height_input"
              onChange={onHeightChange}
              ref={heightInput}
            />
          </div>
        </div>
        <ContinueButton onClick={onContinue} disabled={!canContinue} />
      </div>
    </>
  )
}
